import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Grid, Link, MenuItem, TextField, Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../services/api';
import DataTableCard from '../ui/DataTableCard';
import EnhancedDataGrid from '../ui/EnhancedDataGrid';
import GridCellTooltip from '../ui/GridCellTooltip';
import GroceryOrderLifecycleActions from '../operations/GroceryOrderLifecycleActions';
import { getGroceryStores } from '../../services/businessService';
import { formatMoney } from '../../utils/currency';
import { getDateRangeError } from '../../utils/dateRangeValidation';

export type GroceryOrderScope = 'live' | 'history';

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  delivered: 'success', cancelled: 'error', refunded: 'warning', pending: 'warning',
  confirmed: 'primary', preparing: 'info', ready: 'info', picked_up: 'info', delivering: 'info',
  completed: 'success',
};

const HISTORY_STATUSES = ['delivered', 'cancelled', 'refunded', 'completed'];
const PAYMENT_OPTIONS = ['cash', 'card', 'upi', 'wallet', 'corporate', 'online'];

function formatItems(row: { items?: Array<Record<string, unknown>> }) {
  const items = row.items ?? [];
  if (!items.length) return '—';
  return items.map((item) => {
    const name = String(item.name ?? item.productName ?? 'Item');
    const qty = Number(item.quantity ?? 1);
    return `${name} x${qty}`;
  }).join('; ');
}

function personLabelWithPhone(v: unknown): string {
  if (!v) return '—';
  if (typeof v === 'string') return v;
  const o = v as { firstName?: string; lastName?: string; name?: string; phone?: string; email?: string; _id?: string };
  const name = [o.firstName, o.lastName].filter(Boolean).join(' ').trim() || o.name || '';
  const phone = String(o.phone || '').trim();
  const email = String(o.email || '').trim();
  // Avoid bare generic names like "User" without an identifier
  if (name && phone) return `${name} · ${phone}`;
  if (name && email) return `${name} · ${email}`;
  if (name && name.toLowerCase() !== 'user') return name;
  if (phone) return phone;
  if (email) return email;
  if (o._id) return `Customer · ${String(o._id).slice(-6)}`;
  return name || '—';
}

function formatPaymentLabel(method: unknown): string {
  const m = String(method || '').trim();
  if (!m) return '—';
  const lower = m.toLowerCase();
  if (lower === 'cod' || lower === 'cash_on_delivery') return 'Cash';
  return lower.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatStatusLabel(status: unknown): string {
  const key = String(status || '').toLowerCase();
  if (!key) return '—';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateTime(value: unknown): string {
  if (!value) return '—';
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function entityId(v: unknown): string | undefined {
  if (!v) return undefined;
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (typeof v === 'object' && v !== null && '_id' in v) {
    const id = (v as { _id?: unknown })._id;
    if (id != null && String(id).trim()) return String(id);
  }
  return undefined;
}

type HistoryEventRow = {
  status?: string;
  deliveredAt?: string;
  updatedAt?: string;
  cancelledAt?: string;
  metadata?: {
    cancelledAt?: string;
    refundedAt?: string;
    refundAmount?: number;
    partialRefund?: number;
  };
};

function historyEventParts(row: HistoryEventRow): { primary: string; secondary?: string } {
  const status = String(row.status || '').toLowerCase();
  if (status === 'refunded') {
    const refundedAt = formatDateTime(row.metadata?.refundedAt || row.updatedAt);
    const deliveredAt = formatDateTime(row.deliveredAt);
    return {
      primary: refundedAt === '—' ? 'Refunded Date/Time: —' : `Refunded Date/Time: ${refundedAt}`,
      secondary: deliveredAt === '—' ? undefined : `Delivered Date/Time: ${deliveredAt}`,
    };
  }
  if (status === 'cancelled') {
    const when = formatDateTime(row.cancelledAt || row.metadata?.cancelledAt || row.updatedAt);
    return { primary: when === '—' ? 'Cancelled Date/Time: —' : `Cancelled Date/Time: ${when}` };
  }
  const when = formatDateTime(row.deliveredAt || row.updatedAt);
  return { primary: when === '—' ? 'Delivered Date/Time: —' : `Delivered Date/Time: ${when}` };
}

function historyEventLabel(row: HistoryEventRow): string {
  const { primary, secondary } = historyEventParts(row);
  return secondary ? `${primary} · ${secondary}` : primary;
}

type Props = {
  scope: GroceryOrderScope;
  title: string;
  queryKeyPrefix?: string;
};

export default function GroceryOrdersListPanel({ scope, title, queryKeyPrefix = 'grocery-orders' }: Props) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [storeId, setStoreId] = useState('');
  const [search, setSearch] = useState('');

  const dateRangeError = getDateRangeError(fromDate, toDate);
  const queryKey = [queryKeyPrefix, scope, page, pageSize, fromDate, toDate, status, paymentMethod, storeId, search];

  const { data: stores = [] } = useQuery({
    queryKey: ['grocery-history-stores'],
    queryFn: () => getGroceryStores(),
    enabled: scope === 'history',
    staleTime: 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get('/grocery/orders', {
        params: {
          scope,
          page: page + 1,
          limit: pageSize,
          ...(status ? { status } : {}),
          ...(fromDate ? { fromDate, from: fromDate } : {}),
          ...(toDate ? { toDate, to: toDate } : {}),
          ...(paymentMethod ? { paymentMethod } : {}),
          ...(storeId ? { storeId } : {}),
          ...(search.trim() ? { search: search.trim(), q: search.trim() } : {}),
        },
      });
      return res.data;
    },
    enabled: !dateRangeError,
    refetchInterval: scope === 'live' ? 8000 : false,
    placeholderData: keepPreviousData,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [queryKeyPrefix] }),
    [queryClient, queryKeyPrefix],
  );

  const rows = dateRangeError ? [] : (data?.data || []);
  const total = data?.meta?.total ?? data?.pagination?.total ?? data?.total ?? rows.length;
  const hasFilters = Boolean(fromDate || toDate || status || paymentMethod || storeId || search.trim());

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setStatus('');
    setPaymentMethod('');
    setStoreId('');
    setSearch('');
    setPage(0);
  };

  const onFromDateChange = (value: string) => {
    setFromDate(value);
    if (value && toDate && toDate < value) setToDate(value);
    setPage(0);
  };

  const onToDateChange = (value: string) => {
    if (fromDate && value && value < fromDate) {
      setToDate(fromDate);
      return;
    }
    setToDate(value);
    setPage(0);
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'orderNumber',
      headerName: 'Order No.',
      minWidth: 200,
      width: 210,
      flex: 0.9,
      renderCell: (p) => {
        const label = String(p.row.orderNumber || p.row._id || '—');
        return (
          <Link
            component={RouterLink}
            to={`/orders/${p.row._id}`}
            underline="hover"
            variant="body2"
            fontWeight={700}
            onClick={(e) => e.stopPropagation()}
            title={label}
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {label}
          </Link>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      width: 150,
      valueGetter: (_v, row) => formatStatusLabel(row.status),
      renderCell: (p) => (
        <Chip
          size="small"
          label={formatStatusLabel(p.row.status)}
          color={statusColors[String(p.row.status || '').toLowerCase()] || 'default'}
          sx={{ fontWeight: 700, textTransform: 'none' }}
        />
      ),
    },
    {
      field: 'paymentMethod',
      headerName: 'Payment',
      minWidth: 120,
      width: 130,
      valueGetter: (_v, row) => formatPaymentLabel(row.paymentMethod),
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '—')} />,
    },
    {
      field: 'total',
      headerName: 'Amount',
      minWidth: 120,
      width: 130,
      valueGetter: (_v, row) => formatMoney(row.total ?? 0),
      renderCell: (p) => (
        <Typography variant="body2" fontWeight={800} sx={{ whiteSpace: 'nowrap' }}>
          {String(p.value ?? '—')}
        </Typography>
      ),
    },
    {
      field: 'customerId',
      headerName: 'Customer',
      flex: 1.1,
      minWidth: 200,
      valueGetter: (_v, row) => personLabelWithPhone(row.customerId),
      renderCell: (p) => {
        const label = personLabelWithPhone(p.row.customerId);
        const id = entityId(p.row.customerId);
        if (id && label !== '—') {
          return (
            <Link
              component={RouterLink}
              to={`/customers/${id}`}
              underline="hover"
              variant="body2"
              fontWeight={700}
              onClick={(e) => e.stopPropagation()}
              title={label}
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {label}
            </Link>
          );
        }
        return <GridCellTooltip value={label} />;
      },
    },
    {
      field: 'storeId',
      headerName: 'Store',
      flex: 1.2,
      minWidth: 220,
      valueGetter: (_v, row) => row.storeId?.name || '—',
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '—')} />,
    },
    {
      field: 'partner',
      headerName: 'Delivery Partner',
      flex: 1,
      minWidth: 190,
      valueGetter: (_v, row) => {
        const label = personLabelWithPhone(row.deliveryPartnerId);
        return label === '—' ? 'Unassigned' : label;
      },
      renderCell: (p) => {
        const label = personLabelWithPhone(p.row.deliveryPartnerId);
        if (label === '—') {
          return <Chip size="small" label="Unassigned" variant="outlined" sx={{ fontWeight: 700 }} />;
        }
        return <GridCellTooltip value={label} />;
      },
    },
    ...(scope === 'history' ? [{
      field: 'eventAt',
      headerName: 'Closed Date/Time',
      description: 'Cancelled, Refunded, or Delivered date/time',
      minWidth: 300,
      width: 320,
      flex: 1.2,
      valueGetter: (_v: unknown, row: HistoryEventRow) => historyEventLabel(row),
      renderCell: (p: { row: HistoryEventRow }) => {
        const { primary, secondary } = historyEventParts(p.row);
        return (
          <Box sx={{ py: 0.5, lineHeight: 1.25, overflow: 'hidden', width: '100%' }}>
            <Typography variant="body2" fontWeight={700} noWrap title={primary}>
              {primary}
            </Typography>
            {secondary && (
              <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap title={secondary} display="block">
                {secondary}
              </Typography>
            )}
          </Box>
        );
      },
    } as GridColDef] : []),
    {
      field: 'items',
      headerName: 'Items',
      flex: 1.3,
      minWidth: 220,
      valueGetter: (_v, row) => formatItems(row),
      renderCell: (p) => <GridCellTooltip value={formatItems(p.row)} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: scope === 'history' ? 220 : 240,
      minWidth: 180,
      sortable: false,
      renderCell: (p) => (
        <Box display="flex" alignItems="center" gap={0.5} sx={{ width: '100%', overflow: 'visible' }}>
          <GroceryOrderLifecycleActions order={p.row} onUpdated={invalidate} showAdminExtras />
        </Box>
      ),
    },
  ], [scope, invalidate]);

  return (
    <Box>
      {scope === 'history' && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>
            Filter order history
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1.5 }}>
            Filter by date range, status (Delivered / Cancelled / Refunded), store, payment, or search order number.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From date"
                value={fromDate}
                onChange={(e) => onFromDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: toDate || undefined }}
                slotProps={{ htmlInput: { max: toDate || undefined } }}
                error={!!dateRangeError}
                helperText={dateRangeError ? 'Must be on or before To date' : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To date"
                key={`to-${fromDate || 'none'}`}
                value={toDate}
                onChange={(e) => onToDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDate || undefined }}
                slotProps={{ htmlInput: { min: fromDate || undefined } }}
                error={!!dateRangeError}
                helperText={dateRangeError || undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {HISTORY_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{formatStatusLabel(s)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Store"
                value={storeId}
                onChange={(e) => { setStoreId(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All stores</MenuItem>
                {(stores as Array<{ _id: string; name: string }>).map((s) => (
                  <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Payment type"
                value={paymentMethod}
                onChange={(e) => { setPaymentMethod(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {PAYMENT_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>{formatPaymentLabel(s)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search orders"
                placeholder="Order #, address, city"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
            </Grid>
            <Grid item>
              <Button variant="outlined" color="error" size="small" onClick={clearFilters} disabled={!hasFilters}>
                Clear
              </Button>
            </Grid>
          </Grid>
          {dateRangeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dateRangeError}. Select an end date on or after the start date.
            </Alert>
          )}
        </Box>
      )}

      <DataTableCard title={title}>
        <EnhancedDataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          getRowId={(r) => r._id}
          paginationMode="server"
          rowCount={total}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 50]}
          disableLocalSearch
          searchPlaceholder="Search orders"
          emptyTitle="No data found"
          emptySubtitle={
            dateRangeError
              ? 'Fix the date range to load order history'
              : hasFilters
                ? 'No orders match the selected filters. Try Delivered, Cancelled, or Refunded.'
                : 'No closed grocery orders yet'
          }
          pinnedColumns={{ left: ['__sno', 'orderNumber', 'status'] }}
          columnVisibilityModel={{
            orderNumber: true,
            status: true,
            paymentMethod: true,
            total: true,
            customerId: true,
            storeId: true,
            partner: true,
            eventAt: true,
            items: true,
          }}
        />
      </DataTableCard>
    </Box>
  );
}
